import { useEffect, useRef, useState } from 'react';

const MIMIC_I18N_KEY_BY_ID = Object.freeze({
  innocence: 'exploreUnknownPage.bossIntro.innocence',
  fear: 'exploreUnknownPage.bossIntro.fear',
  pride: 'exploreUnknownPage.bossIntro.pride',
  hunger: 'exploreUnknownPage.bossIntro.hunger',
  thirst: 'exploreUnknownPage.bossIntro.thirst',
  mirth: 'exploreUnknownPage.bossIntro.mirth',
  harmony: 'exploreUnknownPage.bossIntro.harmony',
  awe: 'exploreUnknownPage.bossIntro.awe',
  mortality: 'exploreUnknownPage.bossIntro.mortality',
  creation: 'exploreUnknownPage.bossIntro.creation',
  instinct: 'exploreUnknownPage.bossIntro.instinct',
  kinship: 'exploreUnknownPage.bossIntro.kinship',
  humanity: 'exploreUnknownPage.bossIntro.humanity',
});

/**
 * Centered boss-name toast when Shadow Negara mimics a prior EU boss (mission 75).
 *
 * @param {object | null | undefined} gameState
 * @returns {{ visible: boolean, translationKey: string | null }}
 */
export function useExploreUnknownNegaraBossMimicToast(gameState) {
  const [visible, setVisible] = useState(false);
  const [translationKey, setTranslationKey] = useState(null);
  const seenSequencesRef = useRef(new Set());
  const hideTimerRef = useRef(null);

  useEffect(() => {
    if (Number(gameState?.exploreUnknownMissionIndex) !== 75) {
      setVisible(false);
      setTranslationKey(null);
      seenSequencesRef.current = new Set();
      return undefined;
    }

    const log = gameState?.actionLog;
    if (!Array.isArray(log)) {
      return undefined;
    }

    let newestEntry = null;
    for (const entry of log) {
      if (!entry || entry.type !== 'eu-negara-boss-mimic') continue;
      const seq = entry.sequence;
      if (seq == null || seenSequencesRef.current.has(seq)) continue;
      if (
        !newestEntry ||
        (typeof newestEntry.sequence === 'number' && seq > newestEntry.sequence)
      ) {
        newestEntry = entry;
      }
    }
    if (newestEntry) {
      seenSequencesRef.current.add(newestEntry.sequence);
      const key = MIMIC_I18N_KEY_BY_ID[newestEntry.mimicId];
      if (key) {
        setTranslationKey(key);
        setVisible(true);
        if (hideTimerRef.current != null) {
          window.clearTimeout(hideTimerRef.current);
        }
        hideTimerRef.current = window.setTimeout(() => {
          setVisible(false);
          setTranslationKey(null);
          hideTimerRef.current = null;
        }, 2800);
      }
    }

    return () => {
      if (hideTimerRef.current != null) {
        window.clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };
  }, [gameState?.actionLog, gameState?.exploreUnknownMissionIndex]);

  return { visible, translationKey };
}
